import type { OrderDashboardIssue } from '@/api/hooks/useOrderDashboard'
import { ISSUE_CATEGORY_LABELS } from '@/pages/Dashboard/panels/ConnectionLogsPanel/components/LogDetailsPanel/categories'
import type { components } from '@/types/api/v1'
import { uniqueIssueIds, withoutEchoes } from './issueFields'

type Severity = components['schemas']['Severity']

/** Which count card is selected; each one narrows the issue list below it. */
export type IssueBucketKey = 'issues' | Severity | 'warnings'

/**
 * The cards on screen. `warnings` is deliberately absent -- it read as a
 * second, competing total next to the severities. countIssues still buckets
 * them, so putting the card back is this list plus flipping the request's
 * `warnings` flag in useOrderDashboardIssues.
 */
export const ISSUE_BUCKET_KEYS: IssueBucketKey[] = [
  'issues',
  'high',
  'medium',
  'low',
]

export interface CategoryCount {
  category: string
  label: string
  count: number
}

export interface IssueBucket {
  total: number
  categories: CategoryCount[]
}

export type IssueCounts = Record<IssueBucketKey, IssueBucket>

/**
 * Category rows keep the same order in every card so the five cards read as
 * columns rather than five unrelated lists. ISSUE_CATEGORY_LABELS is already
 * the app's canonical category order; anything the backend adds before this
 * map catches up sorts after the known ones under its raw name.
 */
const CATEGORY_ORDER = Object.keys(ISSUE_CATEGORY_LABELS)

/**
 * The shared labels read "Mapping Issue", "Supply Issue" and so on, which is
 * right in a log grid but says "Issue" twice inside a card already headed
 * ISSUES. Derived rather than duplicated so a relabelling only happens once.
 */
function categoryLabel(category: string): string {
  const label = ISSUE_CATEGORY_LABELS[category]
  return label ? label.replace(/ Issue$/, '') : category
}

/** Counts unique issues, not logs: the same failure on five orders is one. */
function bucket(logs: OrderDashboardIssue[]): IssueBucket {
  const byCategory = new Map<string, Set<unknown>>()
  const all = new Set<unknown>()
  for (const log of logs) {
    const category = log.detail.category
    const ids = byCategory.get(category) ?? new Set<unknown>()
    byCategory.set(category, ids)
    for (const id of uniqueIssueIds(log)) {
      ids.add(id)
      all.add(id)
    }
  }

  const categories = [...byCategory.entries()]
    .map(([category, ids]) => ({
      category,
      label: categoryLabel(category),
      count: ids.size,
    }))
    .sort((a, b) => {
      const aIndex = CATEGORY_ORDER.indexOf(a.category)
      const bIndex = CATEGORY_ORDER.indexOf(b.category)
      if (aIndex !== bIndex) {
        if (aIndex === -1) return 1
        if (bIndex === -1) return -1
        return aIndex - bIndex
      }
      return a.label.localeCompare(b.label)
    })

  return { total: all.size, categories }
}

/**
 * Splits the endpoint's flat list into the five count cards. Everything it
 * returns is either failed or warning, so warnings are what is left once the
 * issues are taken out.
 *
 * The issues bucket is every failure rather than the sum of the three
 * severity buckets. They are the same number whenever severity is set, which
 * it always is in practice -- but a failure that somehow arrived without one
 * belongs in a total the user can see, not silently nowhere.
 */
export function countIssues(
  logs: OrderDashboardIssue[] | undefined,
): IssueCounts {
  const all = withoutEchoes(logs ?? [])
  const warnings = all.filter((log) => log.status === 'warning')
  const issues = all.filter((log) => log.status !== 'warning')
  const withSeverity = (severity: Severity) =>
    bucket(issues.filter((log) => log.severity === severity))

  return {
    issues: bucket(issues),
    high: withSeverity('high'),
    medium: withSeverity('medium'),
    low: withSeverity('low'),
    warnings: bucket(warnings),
  }
}

/** What the count cards currently narrow the list to. */
export interface IssueSelection {
  bucket: IssueBucketKey
  /** A `detail.category` within that bucket, or null for the whole bucket. */
  category: string | null
}

/** Every issue in the window: the state the cards start in. */
export const NO_ISSUE_SELECTION: IssueSelection = {
  bucket: 'issues',
  category: null,
}

/** Whether the cards are narrowing anything -- see NO_ISSUE_SELECTION. */
export function hasIssueSelection(selection: IssueSelection): boolean {
  return selection.bucket !== NO_ISSUE_SELECTION.bucket || !!selection.category
}

/** Which end of the window the list starts from. */
export type IssueSortOrder = 'newest' | 'oldest'

/**
 * The issues behind one count card, optionally narrowed to a single category
 * row within it. The issues bucket is every failure, for the same reason its
 * total is -- see countIssues.
 */
export function filterIssuesByBucket(
  logs: OrderDashboardIssue[] | undefined,
  bucket: IssueBucketKey,
  category?: string | null,
): OrderDashboardIssue[] {
  const all = withoutEchoes(logs ?? [])
  const inBucket = bucketIssues(all, bucket)
  if (!category) return inBucket
  return inBucket.filter((log) => log.detail.category === category)
}

function bucketIssues(
  all: OrderDashboardIssue[],
  bucket: IssueBucketKey,
): OrderDashboardIssue[] {
  if (bucket === 'warnings')
    return all.filter((log) => log.status === 'warning')

  const issues = all.filter((log) => log.status !== 'warning')
  if (bucket === 'issues') return issues
  return issues.filter((log) => log.severity === bucket)
}

/** Compares by when the issue happened, undated issues last either way. */
export function compareIssueDates(
  order: IssueSortOrder,
): (a: OrderDashboardIssue, b: OrderDashboardIssue) => number {
  const time = (log: OrderDashboardIssue) =>
    log.date ? new Date(log.date).getTime() : Number.NaN

  return (a, b) => {
    const aTime = time(a)
    const bTime = time(b)
    if (Number.isNaN(aTime)) return Number.isNaN(bTime) ? 0 : 1
    if (Number.isNaN(bTime)) return -1
    return order === 'newest' ? bTime - aTime : aTime - bTime
  }
}

/** Sorts by when the issue happened, undated issues last either way. */
export function sortIssues(
  logs: OrderDashboardIssue[],
  order: IssueSortOrder,
): OrderDashboardIssue[] {
  return [...logs].sort(compareIssueDates(order))
}
